import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AdminSidebar } from '@/components/Layout/AdminSidebar';
import { useTheme } from '@/hooks/useTheme';
import { useAuthStore } from '@/store/authStore';
import { projectService } from '@/services/projectService';

export function ProjectAnalysesPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const id = projectId ? Number(projectId) : NaN;
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const { user, isRoot, isAdmin } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const getUserInitials = (name: string) =>
    name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

  const { data: project, isLoading } = useQuery({
    queryKey: ['project', id],
    queryFn: () => projectService.getProject(id),
    enabled: Number.isFinite(id),
    retry: 1,
  });

  const headerTitle = useMemo(() => {
    if (!project) return 'Analyses';
    return `Analyses • ${project.title}`;
  }, [project]);

  if (!isRoot() && !isAdmin()) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-500">Accès refusé. Vous devez être administrateur.</p>
        </div>
      </div>
    );
  }

  if (!Number.isFinite(id)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0B1116]">
        <div className="text-center">
          <p className="text-red-500">Identifiant projet invalide</p>
          <button onClick={() => navigate('/admin/projects')} className="mt-4 text-primary hover:text-primary-600">
            Retour aux projets
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0B1116]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-slate-500 dark:text-slate-400">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0B1116]">
        <div className="text-center">
          <p className="text-red-500">Projet non trouvé</p>
          <button onClick={() => navigate('/admin/projects')} className="mt-4 text-primary hover:text-primary-600">
            Retour aux projets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      <AdminSidebar getUserInitials={getUserInitials} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0B1116]">
        <header className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A]">
          <div className="flex items-center gap-4 md:hidden">
            <button onClick={() => setSidebarOpen(true)} className="text-slate-500 hover:text-slate-600 dark:text-slate-400 dark:hover:text-white">
              <span className="material-icons-outlined">menu</span>
            </button>
            <span className="font-bold text-lg dark:text-white">GTB Admin</span>
          </div>
          <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
            <span className="text-slate-400 dark:text-slate-600">Admin</span> / Projets / Analyses
          </div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-slate-900 dark:text-white">{project.title}</span>
            {project.city && (
              <>
                <span className="text-slate-400">•</span>
                <span className="text-sm text-slate-500 dark:text-slate-400">{project.city}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className="p-2 text-slate-400 hover:text-slate-500 dark:hover:text-white transition-colors"
              title={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
              {isDark ? <span className="material-icons-outlined">light_mode</span> : <span className="material-icons-outlined">dark_mode</span>}
            </button>
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-full bg-gradient-to-tr from-primary to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                {user?.fullName ? getUserInitials(user.fullName) : 'AD'}
              </div>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6 md:p-8">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{headerTitle}</h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Page en cours de construction.</p>
          </div>

          <div className="bg-white dark:bg-[#16202A] shadow-sm rounded-xl border border-slate-200 dark:border-[#1E293B] overflow-hidden">
            <div className="p-6 text-slate-600 dark:text-slate-300">
              Contenu à venir.
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

